import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLastRegInfo, setCalculation, setPaymentInfo, setSavedVisitDates, setPendingEditPayload } from '../../../redux/slices/ykVisitSlice';
import ykVisitService from '../services/ykVisitService';
import PaymentErrorModal from '../../../components/PaymentErrorModal';
import { PROFILE_PATH } from '../../../utils/paymentErrors';
import './YkVisitPayment.css';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const getAvatarImage = (gender) => {
  const g = (gender || '').toLowerCase().trim();
  const isMale = g === 'male' || g === 'm';
  const isFemale = g === 'female' || g === 'f';
  const grad = isMale
    ? '<stop offset="0%" style="stop-color:#60a5fa"/><stop offset="100%" style="stop-color:#3b82f6"/>'
    : isFemale
    ? '<stop offset="0%" style="stop-color:#f9a8d4"/><stop offset="100%" style="stop-color:#ec4899"/>'
    : '<stop offset="0%" style="stop-color:#cbd5e1"/><stop offset="100%" style="stop-color:#94a3b8"/>';
  const emoji = isMale ? '\ud83d\udc66' : isFemale ? '\ud83d\udc67' : '\ud83d\udc64';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">${grad}</linearGradient></defs><circle cx="100" cy="100" r="95" fill="url(#g)"/><text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">${emoji}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const YkVisitPayment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { paymentInfo, pendingEditPayload } = useSelector((state) => state.ykVisit);

  // ── All hooks BEFORE any early return ─────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [lookingUpBank, setLookingUpBank] = useState(false);
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    zipCode: '',
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    bankName: '',
  });

  // Populate form defaults when paymentInfo becomes available
  useEffect(() => {
    if (paymentInfo) {
      const fullName = `${paymentInfo.userFirstName || ''} ${paymentInfo.userLastName || ''}`.trim();
      setFormData((prev) => ({
        ...prev,
        cardholderName: prev.cardholderName || fullName,
        accountHolderName: prev.accountHolderName || fullName,
        zipCode: prev.zipCode || paymentInfo.zipCode || '',
      }));
    }
  }, [paymentInfo]);

  // Redirect if no paymentInfo
  useEffect(() => {
    if (!paymentInfo) navigate('/yk-visit/registration', { replace: true });
  }, [paymentInfo, navigate]);

  // ── reCAPTCHA ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || !paymentInfo) return;
    const ensureScript = () =>
      new Promise((resolve) => {
        if (window.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) { existing.addEventListener('load', () => resolve()); return; }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true; s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });

    const renderWidget = () => {
      const el = document.getElementById('ykpay-recaptcha');
      if (!el || recaptchaWidgetId !== null) return;
      const poll = () => {
        const api = window.grecaptcha;
        if (!api) { setTimeout(poll, 150); return; }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') { setTimeout(doRender, 150); return; }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) { setTimeout(doRender, 200); }
        };
        if (typeof api.ready === 'function') { api.ready(doRender); } else { setTimeout(doRender, 0); }
      };
      poll();
    };

    let mounted = true;
    ensureScript().then(() => { if (mounted) setTimeout(renderWidget, 0); });
    return () => { mounted = false; };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId, paymentInfo]);

  // ── Early return AFTER all hooks ───────────────────────────────────────────
  if (!paymentInfo) return null;

  const {
    campId,
    campName,
    userId,
    userFirstName = '',
    userLastName = '',
    userEmail = '',
    userPhone = '',
    studentName = 'Visitor',
    studentGender = '',
    registrationId,
    totalAmount,
    regFee = 0,
    accommodationAmt = 0,
    nights = 0,
    pricePerNight = 0,
    visitDates = [],
    centerId = '',
    streetAddress1 = '',
    streetAddress2 = '',
    city = '',
    zipCode: userZip = '',
    state: userState = 'GA',
    country = 'US',
    entity = 'RMS',
  } = paymentInfo;

  const fullName = `${userFirstName} ${userLastName}`.trim();

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'cardNumber') {
      v = v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
    } else if (name === 'expiryDate') {
      v = v.replace(/\D/g, '').slice(0, 4);
      if (v.length > 2) v = v.slice(0, 2) + '/' + v.slice(2);
    } else if (name === 'cvv') {
      v = v.replace(/\D/g, '').slice(0, 4);
    } else if (name === 'zipCode') {
      v = v.replace(/[^0-9-]/g, '').slice(0, 10);
    } else if (name === 'routingNumber') {
      v = v.replace(/\D/g, '').slice(0, 9);
      if (v.length === 9) lookupBank(v);
    } else if (name === 'accountNumber') {
      v = v.replace(/\D/g, '').slice(0, 17);
    }
    setFormData((p) => ({ ...p, [name]: v }));
    if (validationErrors[name]) setValidationErrors((p) => { const n = { ...p }; delete n[name]; return n; });
  };

  const lookupBank = async (routingNum) => {
    if (!routingNum || routingNum.length !== 9) return;
    setLookingUpBank(true);
    try {
      const name = await ykVisitService.getBankName(routingNum);
      if (name) setFormData((p) => ({ ...p, bankName: name }));
    } catch (_) {/* ignored */}
    finally { setLookingUpBank(false); }
  };

  const handleMethodChange = (method) => {
    setPaymentMethod(method);
    setValidationErrors({});
    if (RECAPTCHA_SITE_KEY && recaptchaWidgetId !== null && window.grecaptcha?.reset) {
      window.grecaptcha.reset(recaptchaWidgetId);
      setRecaptchaToken('');
    }
  };

  const validateCC = () => {
    const errors = {};
    const rawCard = formData.cardNumber.replace(/\s/g, '');
    if (!rawCard) errors.cardNumber = 'Card number is required';
    else if (!/^\d{13,16}$/.test(rawCard)) errors.cardNumber = 'Enter a valid 13–16 digit card number';

    if (!formData.expiryDate) {
      errors.expiryDate = 'Expiry date is required';
    } else {
      const [mm, yy] = formData.expiryDate.split('/');
      const m = parseInt(mm, 10), y = parseInt('20' + yy, 10);
      const now = new Date();
      if (!mm || !yy || isNaN(m) || isNaN(y) || m < 1 || m > 12)
        errors.expiryDate = 'Use MM/YY format';
      else if (new Date(y, m) < new Date(now.getFullYear(), now.getMonth() + 1))
        errors.expiryDate = 'Card has expired';
    }

    if (!formData.cvv) errors.cvv = 'CVV is required';
    else if (!/^\d{3,4}$/.test(formData.cvv)) errors.cvv = 'CVV must be 3 or 4 digits';

    if (!formData.cardholderName?.trim()) errors.cardholderName = 'Cardholder name is required';

    const zip = formData.zipCode?.trim();
    if (!zip) errors.zipCode = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(zip)) errors.zipCode = 'Enter a valid ZIP code (e.g. 30096)';

    return errors;
  };

  const validateACH = () => {
    const errors = {};
    if (!formData.accountHolderName?.trim()) errors.accountHolderName = 'Account holder name is required';
    if (!formData.routingNumber || formData.routingNumber.length !== 9) errors.routingNumber = 'Enter a valid 9-digit routing number';
    if (!formData.accountNumber || formData.accountNumber.length < 5) errors.accountNumber = 'Enter a valid account number (5–17 digits)';
    if (!formData.bankName?.trim()) errors.bankName = 'Bank name is required';
    const zip = formData.zipCode?.trim();
    if (!zip) errors.zipCode = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(zip)) errors.zipCode = 'Enter a valid ZIP code (e.g. 30096)';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setValidationErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
      document.getElementById('ykpay-recaptcha')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const errors = paymentMethod === 'credit' ? validateCC() : validateACH();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const firstErrorKey = Object.keys(errors)[0];
      document.querySelector(`[name="${firstErrorKey}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setProcessing(true);
    try {
      const paymentDetails = {
        paymentType: paymentMethod === 'credit' ? 'CreditCard' : 'ACH',
        amount: totalAmount,
        campId,
        campName,
        userId,
        userEmail,
        userFirstName,
        userLastName,
        userPhone,
        streetAddress1,
        streetAddress2,
        city,
        state: userState,
        zipCode: formData.zipCode || userZip,
        country,
        entity,
        ...(paymentMethod === 'credit'
          ? {
            cardNumber: formData.cardNumber.replace(/\s/g, ''),
            expiryDate: formData.expiryDate,
            cvv: formData.cvv,
          }
          : {
            routingNumber: formData.routingNumber,
            accountNumber: formData.accountNumber,
            accountType: formData.accountType,
            bankName: formData.bankName,
          }),
      };

      const resp = await ykVisitService.processPayment(paymentDetails);
      const approved =
        resp?.createTransactionResponse?.transactionResponse?.responseCode === '1' ||
        resp?.createTransactionResponse?.messages?.resultCode === 'Ok';

      if (resp && approved) {
        const transId = resp?.createTransactionResponse?.transactionResponse?.transId || '';

        if (pendingEditPayload) {
          await ykVisitService.updateVisitDetail(pendingEditPayload);
          dispatch(setPendingEditPayload(null));
        } else {
          await ykVisitService.updatePaymentStatus({
            Camp_Id: parseInt(campId),
            User_Id: parseInt(userId),
            Camp_Payment_Id: registrationId,
            Camp_Order_Id: 0,
            Receipt_Id: transId || '0',
          });
          dispatch(setLastRegInfo(null));
          dispatch(setCalculation(null));
          dispatch(setSavedVisitDates([]));
        }

        const visitDateRange = visitDates?.length > 0
          ? visitDates.map((r) => `${fmtDate(r.start_Date)} – ${fmtDate(r.end_Date)}`).join(', ')
          : 'Visit Dates Registered';

        ykVisitService.sendVisitConfirmationEmail({
          toEmail: userEmail,
          userName: fullName || 'Visitor',
          userPhone: userPhone || 'N/A',
          campName,
          visitDateRange,
          studentName,
          students: [],
          registrationId,
          transactionDate: new Date().toLocaleString(),
          amount: String(totalAmount),
          paymentType: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          transId,
        }).catch((e) => console.warn('Payment confirm email:', e));

        setPaymentResult({
          transId,
          totalAmount,
          paymentMethod: paymentMethod === 'credit' ? 'Credit/Debit Card' : 'Bank Account (ACH)',
          paymentMethodDetail: paymentMethod === 'credit'
            ? `Card ending in ${formData.cardNumber.replace(/\s/g, '').slice(-4)}`
            : `${formData.bankName || 'Bank'} · ${formData.accountType === 'checking' ? 'Checking' : 'Savings'}`,
          campName,
          visitDateRange,
          visitDates: visitDates || [],
          transactionDate: new Date().toLocaleString(),
          studentName,
          userFullName: fullName,
          userEmail,
          userPhone,
          regFee: parseFloat(regFee) || 0,
          accommodationAmt: parseFloat(accommodationAmt) || 0,
          nights,
          pricePerNight,
          registrationId,
        });
        setShowSuccessModal(true);
      } else {
        const msg =
          resp?.meta?.errors?.errorMessages?.[0]?.message ||
          resp?.createTransactionResponse?.transactionResponse?.errors?.[0]?.errorText ||
          resp?.createTransactionResponse?.messages?.message?.[0]?.text ||
          'Payment was declined. Please check your details and try again.';
        setErrorMessage(msg);
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage('Payment processing failed. Please try again or use a different payment method.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleDone = () => {
    dispatch(setPaymentInfo(null));
    setShowSuccessModal(false);
    navigate(`/yk-visit/registration?campId=${campId}&centerId=${centerId}`);
  };

  const handlePrintReceipt = () => {
    if (!paymentResult) return;
    const r = paymentResult;
    const dateRows = (r.visitDates || []).map((d, i) =>
      `<tr><td style="padding:6px 12px;color:#6b7280;font-size:13px">Visit Date ${(r.visitDates.length > 1 ? ` ${i + 1}` : '')}</td><td style="padding:6px 12px;text-align:right;font-size:13px">${fmtDate(d.start_Date)} – ${fmtDate(d.end_Date)}</td></tr>`
    ).join('');
    const feeRows = [
      r.regFee > 0 ? `<tr><td style="padding:5px 12px;font-size:13px;color:#374151">Registration Fee</td><td style="padding:5px 12px;text-align:right;font-size:13px">$${r.regFee.toFixed(2)}</td></tr>` : '',
      r.accommodationAmt > 0 ? `<tr><td style="padding:5px 12px;font-size:13px;color:#374151">Accommodation${r.nights > 0 ? ` (${r.nights} night${r.nights !== 1 ? 's' : ''} × $${r.pricePerNight}/night)` : ''}</td><td style="padding:5px 12px;text-align:right;font-size:13px">$${r.accommodationAmt.toFixed(2)}</td></tr>` : '',
    ].filter(Boolean).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>YK Visit Receipt – ${r.transId || r.registrationId}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;background:#f3f4f6;padding:24px;color:#1f2937}
  .receipt{max-width:600px;margin:0 auto;background:white;border-radius:12px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,.08)}
  .header{background:white;border-bottom:3px solid #4f46e5;padding:24px 32px;display:flex;align-items:center;gap:16px}
  .logo{width:52px;height:52px;border-radius:50%;border:2px solid #e5e7eb;overflow:hidden;flex-shrink:0;display:flex;align-items:center;justify-content:center}
  .logo img{width:44px;height:44px;object-fit:contain}
  .header-text h1{font-size:18px;font-weight:700;color:#1f2937;margin-bottom:2px}
  .header-text p{font-size:12px;color:#6b7280}
  .success-badge{display:inline-flex;align-items:center;gap:6px;background:#dcfce7;color:#166534;border:1px solid #bbf7d0;border-radius:20px;padding:3px 10px;font-size:11px;font-weight:600;margin-left:auto;white-space:nowrap}
  .body{padding:24px 32px}
  .section{margin-bottom:20px}
  .section-title{font-size:11px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px}
  table{width:100%;border-collapse:collapse}
  .divider{border:none;border-top:1px dashed #e5e7eb;margin:16px 0}
  .total-row td{padding:10px 12px;font-size:15px;font-weight:700;border-top:2px solid #e5e7eb}
  .total-amount{color:#4f46e5;font-size:18px}
  .footer{background:#f9fafb;padding:16px 32px;text-align:center;font-size:12px;color:#9ca3af;border-top:1px solid #e5e7eb}
  .trans-id{background:#f3f4f6;border-radius:6px;padding:8px 12px;font-family:monospace;font-size:13px;color:#1f2937;word-break:break-all;display:inline-block;margin-top:4px}
  @media print{
    body{background:white;padding:0}
    .receipt{box-shadow:none;border-radius:0;max-width:100%}
    .no-print{display:none!important}
  }
</style></head><body>
<div class="receipt">
  <div class="header">
    <div class="logo"><img src="https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png" onerror="this.style.display='none'" alt="RMS"/></div>
    <div class="header-text">
      <h1>Yugal Kunj Visit</h1>
      <p>Radha Madhav Society</p>
    </div>
    <div class="success-badge">✅ Payment Confirmed</div>
  </div>
  <div class="body">
    <div class="section">
      <div class="section-title">Transaction Details</div>
      <table>
        ${r.transId ? `<tr><td style="padding:6px 12px;color:#6b7280;font-size:13px">Transaction ID</td><td style="padding:6px 12px;text-align:right"><span class="trans-id">${r.transId}</span></td></tr>` : ''}
        <tr><td style="padding:6px 12px;color:#6b7280;font-size:13px">Registration #</td><td style="padding:6px 12px;text-align:right;font-size:13px">${r.registrationId}</td></tr>
        <tr><td style="padding:6px 12px;color:#6b7280;font-size:13px">Date &amp; Time</td><td style="padding:6px 12px;text-align:right;font-size:13px">${r.transactionDate}</td></tr>
      </table>
    </div>
    <hr class="divider"/>
    <div class="section">
      <div class="section-title">Bill To</div>
      <table>
        <tr><td style="padding:5px 12px;color:#6b7280;font-size:13px">Name</td><td style="padding:5px 12px;text-align:right;font-size:13px">${r.userFullName}</td></tr>
        ${r.userEmail ? `<tr><td style="padding:5px 12px;color:#6b7280;font-size:13px">Email</td><td style="padding:5px 12px;text-align:right;font-size:13px">${r.userEmail}</td></tr>` : ''}
        ${r.userPhone ? `<tr><td style="padding:5px 12px;color:#6b7280;font-size:13px">Phone</td><td style="padding:5px 12px;text-align:right;font-size:13px">${r.userPhone}</td></tr>` : ''}
        <tr><td style="padding:5px 12px;color:#6b7280;font-size:13px">Visitor</td><td style="padding:5px 12px;text-align:right;font-size:13px">${r.studentName}</td></tr>
      </table>
    </div>
    <hr class="divider"/>
    <div class="section">
      <div class="section-title">Visit Program</div>
      <table>
        <tr><td style="padding:6px 12px;color:#6b7280;font-size:13px">Program</td><td style="padding:6px 12px;text-align:right;font-size:13px">${r.campName}</td></tr>
        ${dateRows}
      </table>
    </div>
    <hr class="divider"/>
    <div class="section">
      <div class="section-title">Fee Breakdown</div>
      <table>
        ${feeRows}
        <tr class="total-row"><td>Total Paid</td><td style="text-align:right" class="total-amount">$${parseFloat(r.totalAmount || 0).toFixed(2)}</td></tr>
      </table>
    </div>
    <hr class="divider"/>
    <div class="section">
      <div class="section-title">Payment Method</div>
      <table>
        <tr><td style="padding:5px 12px;color:#6b7280;font-size:13px">Method</td><td style="padding:5px 12px;text-align:right;font-size:13px">${r.paymentMethod}</td></tr>
        ${r.paymentMethodDetail ? `<tr><td style="padding:5px 12px;color:#6b7280;font-size:13px">Details</td><td style="padding:5px 12px;text-align:right;font-size:13px">${r.paymentMethodDetail}</td></tr>` : ''}
      </table>
    </div>
    <div style="text-align:center;margin-top:16px">
      <button class="no-print" onclick="window.print()" style="background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white;border:none;padding:10px 28px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer">🖨️ Print / Save as PDF</button>
    </div>
  </div>
  <div class="footer">
    <p>Thank you for visiting Yugal Kunj · Radha Madhav Society</p>
    <p style="margin-top:4px">Please keep this receipt for your records · ${r.transactionDate}</p>
  </div>
</div></body></html>`;

    const w = window.open('', '_blank', 'width=700,height=800,scrollbars=yes');
    if (w) { w.document.write(html); w.document.close(); }
    else { alert('Please allow popups for this site to view the receipt.'); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ykpay-page">
      <div className="ykpay-container">

        {/* Back */}
        <button className="ykpay-back-btn" onClick={() => navigate(-1)}>
          ← Back to Visit Registration
        </button>

        {/* Header */}
        <div className="ykpay-header">
          <h1 className="ykpay-title">YK Visit — Complete Payment</h1>
          <p className="ykpay-subtitle">Review your visit summary and enter payment details below</p>
        </div>

        {/* Grid: Summary | Form */}
        <div className="ykpay-grid">

          {/* ── Visit Summary Card ── */}
          <div className="ykpay-summary-card">
            <p className="ykpay-summary-title">📋 Visit Summary</p>

            <div className="ykpay-visitor-row">
              <img src={getAvatarImage(studentGender)} alt={studentName} className="ykpay-visitor-avatar" />
              <div>
                <p className="ykpay-visitor-name">{studentName}</p>
                <p className="ykpay-visitor-label">Visitor</p>
              </div>
            </div>

            <div className="ykpay-summary-row">
              <span className="ykpay-summary-row-label">Visit Program</span>
              <span className="ykpay-summary-row-value">{campName}</span>
            </div>

            {visitDates?.map((r, i) => (
              <div key={i} className="ykpay-summary-row">
                <span className="ykpay-summary-row-label">
                  {visitDates.length > 1 ? `Date ${i + 1}` : 'Visit Dates'}
                </span>
                <span className="ykpay-summary-row-value">
                  {fmtDate(r.start_Date)} – {fmtDate(r.end_Date)}
                </span>
              </div>
            ))}

            <div className="ykpay-summary-divider" />

            {parseFloat(regFee) > 0 && (
              <div className="ykpay-summary-row">
                <span className="ykpay-summary-row-label">Registration Fee</span>
                <span className="ykpay-summary-row-value">${parseFloat(regFee).toFixed(2)}</span>
              </div>
            )}

            {parseFloat(accommodationAmt) > 0 && (
              <div className="ykpay-summary-row">
                <span className="ykpay-summary-row-label">
                  Accommodation
                  {nights > 0 && pricePerNight > 0 && (
                    <span className="ykpay-summary-row-sub">
                      {nights} night{nights !== 1 ? 's' : ''} × ${pricePerNight}/night
                    </span>
                  )}
                </span>
                <span className="ykpay-summary-row-value">${parseFloat(accommodationAmt).toFixed(2)}</span>
              </div>
            )}

            <div className="ykpay-summary-total">
              <span className="ykpay-summary-total-label">Total Due</span>
              <span className="ykpay-summary-total-amount">${parseFloat(totalAmount).toFixed(2)}</span>
            </div>
          </div>

          {/* ── Payment Form Card ── */}
          <div className="ykpay-form-card">
            <p className="ykpay-form-title">Payment Method</p>

            {/* Method tabs */}
            <div className="ykpay-method-tabs">
              <button type="button"
                className={`ykpay-method-tab${paymentMethod === 'credit' ? ' active' : ''}`}
                onClick={() => handleMethodChange('credit')}>
                💳 Credit / Debit Card
              </button>
              <button type="button"
                className={`ykpay-method-tab${paymentMethod === 'ach' ? ' active' : ''}`}
                onClick={() => handleMethodChange('ach')}>
                🏦 Bank Account (ACH)
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {paymentMethod === 'credit' ? (
                <>
                  <p className="ykpay-section-heading">Card Information</p>

                  <div className="ykpay-form-row single">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Card Number *</label>
                      <input name="cardNumber" value={formData.cardNumber} onChange={handleInputChange}
                        placeholder="1234 5678 9012 3456" maxLength="19" autoComplete="cc-number"
                        className={`ykpay-field-input${validationErrors.cardNumber ? ' has-error' : ''}`} />
                      {validationErrors.cardNumber && <p className="ykpay-field-error">{validationErrors.cardNumber}</p>}
                    </div>
                  </div>

                  <div className="ykpay-form-row">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Expiry Date *</label>
                      <input name="expiryDate" value={formData.expiryDate} onChange={handleInputChange}
                        placeholder="MM/YY" maxLength="5" autoComplete="cc-exp"
                        className={`ykpay-field-input${validationErrors.expiryDate ? ' has-error' : ''}`} />
                      {validationErrors.expiryDate && <p className="ykpay-field-error">{validationErrors.expiryDate}</p>}
                    </div>
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">CVV *</label>
                      <input name="cvv" type="password" value={formData.cvv} onChange={handleInputChange}
                        placeholder="•••" maxLength="4" autoComplete="cc-csc"
                        className={`ykpay-field-input${validationErrors.cvv ? ' has-error' : ''}`} />
                      {validationErrors.cvv && <p className="ykpay-field-error">{validationErrors.cvv}</p>}
                    </div>
                  </div>

                  <div className="ykpay-form-row single">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Cardholder Name *</label>
                      <input name="cardholderName" value={formData.cardholderName} onChange={handleInputChange}
                        placeholder="Name on card" autoComplete="cc-name"
                        className={`ykpay-field-input${validationErrors.cardholderName ? ' has-error' : ''}`} />
                      {validationErrors.cardholderName && <p className="ykpay-field-error">{validationErrors.cardholderName}</p>}
                    </div>
                  </div>

                  <div className="ykpay-form-row single">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Billing ZIP Code *</label>
                      <input name="zipCode" value={formData.zipCode} onChange={handleInputChange}
                        placeholder="e.g. 30096" maxLength="10" autoComplete="postal-code"
                        className={`ykpay-field-input${validationErrors.zipCode ? ' has-error' : ''}`} />
                      {validationErrors.zipCode && <p className="ykpay-field-error">{validationErrors.zipCode}</p>}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="ykpay-section-heading">Bank Account Information</p>

                  <div className="ykpay-form-row single">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Account Holder Name *</label>
                      <input name="accountHolderName" value={formData.accountHolderName} onChange={handleInputChange}
                        placeholder="Full name as on bank account"
                        className={`ykpay-field-input${validationErrors.accountHolderName ? ' has-error' : ''}`} />
                      {validationErrors.accountHolderName && <p className="ykpay-field-error">{validationErrors.accountHolderName}</p>}
                    </div>
                  </div>

                  <div className="ykpay-form-row">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Routing Number *</label>
                      <div className="ykpay-bank-lookup">
                        <input name="routingNumber" value={formData.routingNumber} onChange={handleInputChange}
                          placeholder="9-digit routing" maxLength="9"
                          className={`ykpay-field-input${validationErrors.routingNumber ? ' has-error' : ''}`} />
                        {lookingUpBank && <span className="ykpay-bank-lookup-spinner" />}
                      </div>
                      {validationErrors.routingNumber && <p className="ykpay-field-error">{validationErrors.routingNumber}</p>}
                    </div>
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Account Number *</label>
                      <input name="accountNumber" value={formData.accountNumber} onChange={handleInputChange}
                        placeholder="Account number" maxLength="17"
                        className={`ykpay-field-input${validationErrors.accountNumber ? ' has-error' : ''}`} />
                      {validationErrors.accountNumber && <p className="ykpay-field-error">{validationErrors.accountNumber}</p>}
                    </div>
                  </div>

                  <div className="ykpay-form-row">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Account Type</label>
                      <select name="accountType" value={formData.accountType} onChange={handleInputChange}
                        className="ykpay-field-select">
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </select>
                    </div>
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Bank Name *</label>
                      <input name="bankName" value={formData.bankName} onChange={handleInputChange}
                        placeholder="Auto-filled or enter manually"
                        className={`ykpay-field-input${validationErrors.bankName ? ' has-error' : ''}`} />
                      {validationErrors.bankName && <p className="ykpay-field-error">{validationErrors.bankName}</p>}
                    </div>
                  </div>

                  <div className="ykpay-form-row single">
                    <div className="ykpay-form-group">
                      <label className="ykpay-field-label">Billing ZIP Code *</label>
                      <input name="zipCode" value={formData.zipCode} onChange={handleInputChange}
                        placeholder="e.g. 30096" maxLength="10" autoComplete="postal-code"
                        className={`ykpay-field-input${validationErrors.zipCode ? ' has-error' : ''}`} />
                      {validationErrors.zipCode && <p className="ykpay-field-error">{validationErrors.zipCode}</p>}
                    </div>
                  </div>
                </>
              )}

              {/* reCAPTCHA */}
              {RECAPTCHA_SITE_KEY && (
                <div className="ykpay-recaptcha-wrap">
                  <div id="ykpay-recaptcha" />
                  {validationErrors.recaptcha && (
                    <p className="ykpay-recaptcha-error">{validationErrors.recaptcha}</p>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="ykpay-btn-group">
                <button type="button" onClick={() => navigate(-1)} className="ykpay-cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={processing} className="ykpay-submit-btn">
                  {processing ? (
                    <><span className="ykpay-btn-spinner" /> Processing…</>
                  ) : (
                    `Pay $${parseFloat(totalAmount).toFixed(2)}`
                  )}
                </button>
              </div>

              <p className="ykpay-security-note">🔒 Secured by Authorize.Net · 256-bit SSL encryption</p>
            </form>
          </div>
        </div>
      </div>

      {/* ── Processing overlay ── */}
      {processing && (
        <div className="ykpay-processing-overlay">
          <div className="ykpay-processing-inner">
            <div className="ykpay-processing-rings">
              <span className="ykpay-ring ykpay-ring-1" />
              <span className="ykpay-ring ykpay-ring-2" />
              <div className="ykpay-processing-logo">
                <img
                  src="https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png"
                  alt="RMS"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
            <h3 className="ykpay-processing-title">Processing Payment</h3>
            <p className="ykpay-processing-sub">Please wait · do not close this window</p>
            <div className="ykpay-processing-dots">
              {[0, 150, 300].map((d) => (
                <span key={d} className="ykpay-processing-dot" style={{ animationDelay: `${d}ms` }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Success modal ── */}
      {showSuccessModal && (
        <div className="ykpay-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleDone(); }}>
          <div className="ykpay-modal">
            <div className="ykpay-success-header">
              <div className="ykpay-success-icon">✅</div>
              <h2 className="ykpay-success-title">Payment Successful!</h2>
              <p className="ykpay-success-sub">Your visit registration is confirmed</p>
            </div>
            <div className="ykpay-success-body">
              {paymentResult?.transId && (
                <div className="ykpay-success-row">
                  <span className="ykpay-success-row-label">Transaction ID</span>
                  <span className="ykpay-success-row-value ykpay-monospace">{paymentResult.transId}</span>
                </div>
              )}
              <div className="ykpay-success-row">
                <span className="ykpay-success-row-label">Visit Program</span>
                <span className="ykpay-success-row-value">{paymentResult?.campName}</span>
              </div>
              <div className="ykpay-success-row">
                <span className="ykpay-success-row-label">Visitor</span>
                <span className="ykpay-success-row-value">{studentName}</span>
              </div>
              <div className="ykpay-success-row">
                <span className="ykpay-success-row-label">Amount Charged</span>
                <span className="ykpay-success-row-value ykpay-amount-green">
                  ${parseFloat(paymentResult?.totalAmount || 0).toFixed(2)}
                </span>
              </div>
              <div className="ykpay-success-row">
                <span className="ykpay-success-row-label">Payment Method</span>
                <span className="ykpay-success-row-value">{paymentResult?.paymentMethod}</span>
              </div>
              <div className="ykpay-success-row">
                <span className="ykpay-success-row-label">Date & Time</span>
                <span className="ykpay-success-row-value">{paymentResult?.transactionDate}</span>
              </div>
            </div>
            <div className="ykpay-success-footer">
              {userEmail && (
                <p className="ykpay-success-email-note">📧 Confirmation sent to {userEmail}</p>
              )}
              <div className="ykpay-btn-group" style={{ marginTop: '0.5rem' }}>
                <button onClick={handlePrintReceipt} className="ykpay-cancel-btn">
                  🧾 View / Download Receipt
                </button>
                <button onClick={handleDone} className="ykpay-done-btn" style={{ flex: 1 }}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Error modal ── */}
      <PaymentErrorModal
        open={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
        onBack={() => { setShowErrorModal(false); navigate(-1); }}
        backLabel="Cancel"
        onUpdateAddress={() => { setShowErrorModal(false); navigate(PROFILE_PATH); }}
      />
    </div>
  );
};

export default YkVisitPayment;
